import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

// Props base para inputs
interface BaseInputProps {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
}

// Props para Input
interface InputProps extends BaseInputProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

// Props para Textarea
interface TextareaProps extends BaseInputProps, React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  rows?: number;
}

// Props para Select
interface SelectProps extends BaseInputProps, React.SelectHTMLAttributes<HTMLSelectElement> {
  children: React.ReactNode;
}

/**
 * Componente Input seguindo o design system
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  required,
  fullWidth = true,
  size = 'md',
  startIcon,
  endIcon,
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const baseClasses = 'input-field';
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const inputClasses = clsx(
    baseClasses,
    sizeClasses[size],
    {
      'w-full': fullWidth,
      'border-red-300 focus:ring-red-500': error,
      'pl-10': startIcon && size === 'md',
      'pl-8': startIcon && size === 'sm',
      'pl-12': startIcon && size === 'lg',
      'pr-10': endIcon && size === 'md',
      'pr-8': endIcon && size === 'sm',
      'pr-12': endIcon && size === 'lg',
    },
    className
  );

  const iconSizeMap = {
    sm: 16,
    md: 18,
    lg: 20
  };

  const iconPositionClasses = {
    start: {
      sm: 'left-2 top-1.5',
      md: 'left-3 top-2.5',
      lg: 'left-4 top-3.5'
    },
    end: {
      sm: 'right-2 top-1.5',
      md: 'right-3 top-2.5',
      lg: 'right-4 top-3.5'
    }
  };

  return (
    <div className={clsx({ 'w-full': fullWidth })}>
      {label && (
        <label 
          htmlFor={inputId} 
          className="text-label block mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {startIcon && (
          <div className={clsx(
            'absolute text-slate-400 pointer-events-none',
            iconPositionClasses.start[size]
          )}>
            {React.cloneElement(startIcon as React.ReactElement, { 
              size: iconSizeMap[size] 
            })}
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          {...props}
        />
        
        {endIcon && (
          <div className={clsx(
            'absolute text-slate-400 pointer-events-none',
            iconPositionClasses.end[size]
          )}>
            {React.cloneElement(endIcon as React.ReactElement, { 
              size: iconSizeMap[size] 
            })}
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-small text-slate-500 mt-1">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

/**
 * Componente Textarea seguindo o design system
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  helperText,
  required,
  fullWidth = true,
  rows = 4,
  className,
  id,
  ...props
}, ref) => {
  const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  const textareaClasses = clsx(
    'textarea-field',
    {
      'w-full': fullWidth,
      'border-red-300 focus:ring-red-500': error,
    },
    className
  );

  return (
    <div className={clsx({ 'w-full': fullWidth })}>
      {label && (
        <label 
          htmlFor={textareaId} 
          className="text-label block mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        className={textareaClasses}
        {...props}
      />
      
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-small text-slate-500 mt-1">{helperText}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

/**
 * Componente Select seguindo o design system
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  helperText,
  required,
  fullWidth = true,
  children,
  className,
  id,
  ...props
}, ref) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  const selectClasses = clsx(
    'select-field',
    {
      'w-full': fullWidth,
      'border-red-300 focus:ring-red-500': error,
    },
    className
  );

  return (
    <div className={clsx({ 'w-full': fullWidth })}>
      {label && (
        <label 
          htmlFor={selectId} 
          className="text-label block mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <select
        ref={ref}
        id={selectId}
        className={selectClasses}
        {...props}
      >
        {children}
      </select>
      
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-small text-slate-500 mt-1">{helperText}</p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

/**
 * Componente Checkbox seguindo o design system
 */
interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
  label,
  error,
  helperText,
  className,
  id,
  ...props
}, ref) => {
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div>
      <div className="flex items-center">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          className={clsx('checkbox-field', className)}
          {...props}
        />
        {label && (
          <label 
            htmlFor={checkboxId}
            className="text-label ml-2 cursor-pointer"
          >
            {label}
          </label>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-small text-slate-500 mt-1">{helperText}</p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';